import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getStation } from '../api';

function StatCard({ label, value, sub, color='#6366f1', icon, onClick }) {
  return (
    <div onClick={onClick} style={{ background:'#fff', borderRadius:12, padding:20,
      border:'1px solid #f1f5f9', boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
      cursor:onClick?'pointer':'default', transition:'box-shadow 0.15s' }}
      onMouseEnter={e=>onClick&&(e.currentTarget.style.boxShadow='0 4px 16px rgba(99,102,241,0.12)')}
      onMouseLeave={e=>onClick&&(e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)')}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5 }}>{label}</span>
        <div style={{ width:34, height:34, borderRadius:9, display:'flex', alignItems:'center',
          justifyContent:'center', background:`${color}15`, fontSize:17 }}>{icon}</div>
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'#94a3b8', marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function QuickAction({ label, desc, icon, onClick, color='#6366f1' }) {
  return (
    <button onClick={onClick} style={{ background:'#fff', borderRadius:12, padding:18,
      border:'1px solid #f1f5f9', cursor:'pointer', textAlign:'left', transition:'all 0.15s',
      width:'100%' }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor=color; e.currentTarget.style.boxShadow=`0 0 0 3px ${color}20`; }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor='#f1f5f9'; e.currentTarget.style.boxShadow='none'; }}>
      <div style={{ fontSize:22, marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:13, fontWeight:700, color:'#0f172a', marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:12, color:'#64748b', lineHeight:1.4 }}>{desc}</div>
    </button>
  );
}

function PersonaStatusCard({ persona, navigate }) {
  const statusColor = {
    active:'#16a34a', training:'#2563eb', enrolling:'#d97706',
    draft:'#94a3b8', suspended:'#dc2626'
  };
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:36, height:36, borderRadius:'50%',
          background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🎙</div>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{persona.voice_name}</div>
          <div style={{ fontSize:11, color:'#64748b' }}>
            {persona.elevenlabs_clone_status==='ready' ? '✓ Cloned' : '○ Stand-in'} ·{' '}
            {persona.voiceseal_enrolled ? '✓ VoiceSeal' : '○ Not enrolled'}
          </div>
        </div>
      </div>
      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20,
        background:`${statusColor[persona.status] || '#94a3b8'}15`,
        color: statusColor[persona.status] || '#94a3b8' }}>
        {persona.status}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const station  = getStation();
  const [stats, setStats]       = useState(null);
  const [personas, setPersonas] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    Promise.all([
      api.get('/broadcast/dashboard/stats').catch(() => ({
        stations:1, active_sessions:0, shows_this_week:0,
        ad_revenue_month:0, clips_generated_today:0, uptime_pct:99.9
      })),
      api.get('/broadcast/dbp/personas').catch(() => ({ personas:[] })),
      api.get('/broadcast/marketplace/my/licenses').catch(() => ({ licenses:[] })),
    ]).then(([s, p, l]) => {
      setStats(s);
      setPersonas((p.personas || []).slice(0, 4));
      setLicenses((l.licenses || []).filter(lic => lic.status==='active').slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  const activePersonas   = personas.filter(p => p.status==='active').length;
  const clonedPersonas   = personas.filter(p => p.elevenlabs_clone_status==='ready').length;
  const enrolledPersonas = personas.filter(p => p.voiceseal_enrolled).length;

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>

      {/* Welcome banner */}
      <div style={{ background:'linear-gradient(135deg,#1e1b4b 0%,#4338ca 100%)',
        borderRadius:16, padding:'28px 32px', marginBottom:28,
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h2 style={{ margin:0, color:'#fff', fontSize:22, fontWeight:800 }}>
            {greeting} 👋
          </h2>
          <p style={{ margin:'6px 0 0', color:'rgba(255,255,255,0.65)', fontSize:13 }}>
            {station
              ? `${station.callsign} · ${station.city}, ${station.state} · ${station.format}`
              : 'Set up your first station to get started'}
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {!station && (
            <button onClick={()=>navigate('/stations')} style={{ background:'#fff',
              color:'#4338ca', fontWeight:700, fontSize:13, padding:'10px 20px',
              borderRadius:9, border:'none', cursor:'pointer' }}>
              Add Station →
            </button>
          )}
          <button onClick={()=>navigate('/onboarding')} style={{ background:'rgba(255,255,255,0.15)',
            color:'#fff', fontWeight:600, fontSize:12, padding:'10px 16px',
            borderRadius:9, border:'1px solid rgba(255,255,255,0.2)', cursor:'pointer' }}>
            Setup Guide
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:28 }}>
        <StatCard label="Personas" icon="🎙" color="#6366f1"
          value={loading ? '…' : personas.length}
          sub={`${activePersonas} active`}
          onClick={()=>navigate('/personas')} />
        <StatCard label="Voice Clones" icon="🎤" color="#7c3aed"
          value={loading ? '…' : clonedPersonas}
          sub={`${enrolledPersonas} VoiceSeal enrolled`}
          onClick={()=>navigate('/personas')} />
        <StatCard label="Active Licenses" icon="📋" color="#0891b2"
          value={loading ? '…' : licenses.length}
          sub="personas licensed"
          onClick={()=>navigate('/marketplace')} />
        <StatCard label="Clips Today" icon="🎵" color="#16a34a"
          value={loading ? '…' : (stats?.clips_generated_today ?? 0)}
          sub="generated" />
        <StatCard label="Ad Revenue" icon="💰" color="#ea580c"
          value={loading ? '…' : `$${stats?.ad_revenue_month ?? 0}`}
          sub="this month" />
        <StatCard label="Uptime" icon="📡" color="#0284c7"
          value={loading ? '…' : `${stats?.uptime_pct ?? 99.9}%`}
          sub="last 30 days" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>

        {/* Persona status */}
        <div style={{ background:'#fff', borderRadius:14, padding:22, border:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <h4 style={{ margin:0, fontSize:14, fontWeight:700, color:'#0f172a' }}>🎙 My Personas</h4>
            <button onClick={()=>navigate('/personas')} style={{ background:'none', border:'none',
              fontSize:12, color:'#6366f1', fontWeight:600, cursor:'pointer' }}>
              View all →
            </button>
          </div>
          {loading ? (
            <div style={{ color:'#94a3b8', fontSize:13 }}>Loading…</div>
          ) : personas.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🎙</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:12 }}>No personas yet</div>
              <button onClick={()=>navigate('/personas')} style={{ background:'#6366f1',
                color:'#fff', border:'none', borderRadius:8, padding:'8px 16px',
                fontSize:12, fontWeight:600, cursor:'pointer' }}>
                Create First Persona
              </button>
            </div>
          ) : (
            <div>
              {personas.map(p => <PersonaStatusCard key={p.id} persona={p} navigate={navigate} />)}
            </div>
          )}
        </div>

        {/* Licensed personas */}
        <div style={{ background:'#fff', borderRadius:14, padding:22, border:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <h4 style={{ margin:0, fontSize:14, fontWeight:700, color:'#0f172a' }}>📋 Active Licenses</h4>
            <button onClick={()=>navigate('/marketplace')} style={{ background:'none', border:'none',
              fontSize:12, color:'#6366f1', fontWeight:600, cursor:'pointer' }}>
              Marketplace →
            </button>
          </div>
          {loading ? (
            <div style={{ color:'#94a3b8', fontSize:13 }}>Loading…</div>
          ) : licenses.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🤝</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:12 }}>No licensed personas yet</div>
              <button onClick={()=>navigate('/marketplace')} style={{ background:'#6366f1',
                color:'#fff', border:'none', borderRadius:8, padding:'8px 16px',
                fontSize:12, fontWeight:600, cursor:'pointer' }}>
                Browse Marketplace
              </button>
            </div>
          ) : (
            <div>
              {licenses.map(l => (
                <div key={l.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%',
                      background:'linear-gradient(135deg,#0891b2,#0e7490)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🎙</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{l.voice_name}</div>
                      <div style={{ fontSize:11, color:'#64748b' }}>
                        ${l.monthly_fee}/mo · {l.days_remaining !== null ? `${Math.max(0,Math.round(l.days_remaining))}d left` : 'Active'}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:10,
                    background:'#f0fdf4', color:'#16a34a' }}>Active</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Quick Actions</h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:28 }}>
        <QuickAction icon="🎙" label="Create Persona"
          desc="Build a new AI broadcaster persona"
          onClick={()=>navigate('/personas')} color="#6366f1" />
        <QuickAction icon="📅" label="Schedule Program"
          desc="Assign personas to broadcast slots"
          onClick={()=>navigate('/programs')} color="#7c3aed" />
        <QuickAction icon="🤖" label="AI Overnight Host"
          desc="Configure autonomous hosting"
          onClick={()=>navigate('/ai-host')} color="#0891b2" />
        <QuickAction icon="🎚️" label="Order a Show"
          desc="Commission a produced AI show"
          onClick={()=>navigate('/show-factory')} color="#ea580c" />
        <QuickAction icon="🛒" label="Browse Marketplace"
          desc="License voices from other broadcasters"
          onClick={()=>navigate('/marketplace')} color="#16a34a" />
        <QuickAction icon="📊" label="Analytics"
          desc="Listeners, plays, and performance"
          onClick={()=>navigate('/analytics')} color="#0284c7" />
        <QuickAction icon="💰" label="Ad Campaign"
          desc="Create and deploy audio ads"
          onClick={()=>navigate('/ad-platform')} color="#d97706" />
        <QuickAction icon="⚙️" label="Settings"
          desc="Station, billing, and account"
          onClick={()=>navigate('/settings')} color="#64748b" />
      </div>

      {/* DBP feature highlight (shown until first persona created) */}
      {!loading && personas.length === 0 && (
        <div style={{ background:'linear-gradient(135deg,#f0f9ff,#e0f2fe)',
          borderRadius:14, padding:24, border:'1px solid #bae6fd',
          display:'flex', alignItems:'center', gap:24 }}>
          <div style={{ fontSize:48, flexShrink:0 }}>🎙</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:16, fontWeight:800, color:'#0c4a6e', marginBottom:6 }}>
              Meet Digitized Broadcast Personas (DBP)
            </div>
            <div style={{ fontSize:13, color:'#0369a1', lineHeight:1.6 }}>
              Train an AI on your broadcast style, clone your voice, and let it run shifts autonomously.
              Co-anchor with another persona or go live yourself with an AI co-host.
              License your persona to other broadcasters and earn 70% of every clip.
            </div>
          </div>
          <button onClick={()=>navigate('/onboarding')} style={{ background:'#0284c7',
            color:'#fff', border:'none', borderRadius:10, padding:'12px 24px',
            fontSize:13, fontWeight:700, cursor:'pointer', flexShrink:0 }}>
            Get Started →
          </button>
        </div>
      )}

    </div>
  );
}
